<style scoped lang="scss">
.assistance-toggle-label {
  font-size: 1.4em;
  display: flex;

  .checkbox-container {
    width: 30px;
  }

  span {
    margin-left: 8px;

  }

  input[type=checkbox] {
    transform: scale(1.4);
  }
}
</style>

<template>
    <div v-if="application">
        <h2>{{ type.name }}</h2>
        <h4 v-if="type.description !== null" style="margin-top: 0px !important;">
            {{ type.description }}
        </h4>
        <div class="uk-grid">
            <div class="uk-width-1-1">

                <label class="assistance-toggle-label">
                    <div class="checkbox-container">
                        <input type="checkbox" v-model="needsAssistance" class="uk-checkbox" checked>
                    </div>
                    <div>
                        <span>{{ getCheckboxText(type) }}</span>
                    </div>
                </label>

            </div>

            <div class="uk-width-1-1 uk-margin-top" v-if="needsAssistance">
                <div class="uk-grid">
                    <!-- WHo is it paid to? -->
                    <div class="uk-width-small-1-1 uk-width-medium-1-3" v-if="type.is_utility">
                        <fieldset>
                            <label>Who Is This Bill Paid To?</label>
                            <div>
                                <validated-section :errors="errors" field="vendorId">
                                    <autocomplete
                                            :placeholder="'Find Vendor'"
                                            ref="autocomplete"
                                            class="form-autocomplete"
                                            :search="searchVendors"
                                            :get-result-value="getResultValue"
                                            :debounce-time="200"
                                            @submit="setVendor"
                                            @focus="vendor = null"
                                            @blur="setVendorFromTextValue"
                                    >
                                        <template #result="{result, props}">
                                            <li v-bind="props">
                                                <p>{{ result.organization_name }}</p>
                                            </li>
                                        </template>
                                    </autocomplete>
                                </validated-section>

                            </div>
                        </fieldset>
                    </div>

                    <!-- WHo pays it? -->
                    <div class="uk-width-small-1-1 uk-width-medium-1-3">
                        <fieldset>
                            <label>Who Pays This Bill?</label>
                            <div>
                                <validated-section :errors="errors" field="hhMemberId">
                                    <select class="uk-input uk-select uk-form-large uk-width-1-1" v-model="hhMemberId">
                                        <option :value="null">Select a Household Member</option>
                                        <option v-for="member in application.household.members" :value="member.id">
                                            {{ member.first_name }}
                                            {{ member.last_name }}
                                        </option>
                                    </select>
                                </validated-section>
                            </div>
                        </fieldset>
                    </div>

                    <!-- Disconnected? -->
                    <div class="uk-width-small-1-1 uk-width-medium-1-3" v-show="type.is_utility">
                        <fieldset>
                            <label class="">Is This Utility Disconnected?</label><br/>
                            <validated-section :errors="errors" field="disconnectStatusId">
                                <select v-model="disconnectStatusId" required
                                        class="uk-width-1-1 uk-select uk-form-large">
                                    <option value="" selected>_</option>
                                    <option v-for="item in assets.disconnectedStatus" :value="item.id">
                                        {{ item.disconnected_statuse_desc }}
                                    </option>
                                </select>
                            </validated-section>
                        </fieldset>
                    </div>
                </div>


                <div class="uk-grid" v-if="type.is_utility">
                    <!-- Account Number -->
                    <div class="uk-width-small-1-1 uk-width-medium-1-3">
                        <fieldset>
                            <label class="">Account Number</label><br/>
                            <validated-section :errors="errors" field="accountNumber">
                                <input type="text" class="uk-form-large uk-width-1-1" v-model="accountNumber"/>
                            </validated-section>
                        </fieldset>
                    </div>

                    <!-- Account Number Confirmation -->
                    <div class="uk-width-small-1-1 uk-width-medium-1-3" v-show="type.is_utility">
                        <fieldset>
                            <label class="">Confirm Account Number</label><br/>
                            <validated-section :errors="errors" field="accountNumberConfirmation">
                                <input type="text" class="uk-form-large uk-width-1-1"
                                       v-model="accountNumberConfirmation">
                            </validated-section>
                        </fieldset>
                    </div>
                </div>
            </div>

            <div class="uk-width-1-1 uk-margin-top">
                <slot v-if="needsAssistance"/>
            </div>

            <div class="uk-width-1-1 uk-margin-top" v-if="type.pivot.type === 1 && needsAssistance && canShowLedger">
                <div class="uk-overflow-container uk-hidden-small uk-grid-width-medium-1-1">
                    <table class="uk-table uk-table-divider uk-table-striped">
                        <thead>
                        <tr>
                            <th class="uk-text-left">Month<span v-if="futureMonths() > 1">*</span></th>
                            <th class="uk-text-right">{{ programSettings['ledger-title-amount-due'] }}</th>
                            <th class="uk-text-right">{{ programSettings['ledger-title-amount-paid'] }}</th>
                            <th class="uk-text-right" v-if="program.settings['uses-ledger-entry-fees']">
                                {{ programSettings['ledger-title-amount-fees-due'] }}
                                <span v-if="type.pivot.max_fee_amount" class="uk-text-muted">
                                    (Max {{ type.pivot.max_fee_amount | formatMoney }})
                                </span>
                            </th>

                            <th class="uk-text-right" v-if="showAlternativeFunding">
                                Amount
                                Paid by Other Program(s)
                                <a uk-tooltip title="WHAT DOES THIS MEAN?"
                                   v-if="programSettings['show-housing-help-button']"
                                   onclick="UIkit.modal.alert('<div class=\'uk-grid\'><div class=\'uk-width-1-1 uk-margin-bottom\'><h3>If you have received assistance from another government program specifically for rent or utilities, please enter that amount here. (Do not include rent subsidies from programs like Section 8).</h3></div></div>');"
                                   class="uk-dark uk-light use-hand-cursor theme-primary-bg-color uk-float-right"
                                   style="margin-right: 110px; font-size: 14px; background-color: #6495d1; width: 20px; height: 20px; display: block; border-radius: 10px; text-align: center; color:white">?</a>
                            </th>
                            <th class="uk-text-right">{{ programSettings['ledger-title-amount-total-requested'] }}</th>
                            <template v-if="permissions.processor">
                                <th>Approved</th>
                                <th>Paid</th>
                            </template>

                            <!-- <th>Invoiced Amount</th>
                            <th>Approved Amount</th>
                            <th>Paid Amount</th> -->
                        </tr>
                        </thead>
                        <tbody>
                        <template v-for="(month, key) in months">
                        <tr :key="key">
                            <td class="uk-text-left">
                                <input type="checkbox" :value="month.format(monthFormat)"
                                       @change="onMonthCheckedChange(month, $event)"
                                       v-model="checkedMonths"/>
                                {{ month.format('MMM YYYY') }}
                            </td>
                            <td class="uk-text-right">
                                <div v-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)] && !getValueForMonth(month, 'has_payment') && canEditLedgerAmounts">
                                    <currency-input :currency="{ prefix: '$ '}" v-if="canChangeAmount"
                                           @change="updateValueForMonth(month, 'amount', $event)"
                                           :value="getValueForMonth(month, 'amount')"/>
                                    <span v-else class="uk-text-right">{{ getValueForMonth(month, 'amount') | formatMoney}}</span>

                                </div>
                                <div v-else-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]">
                                    {{ getValueForMonth(month, 'amount') | formatMoney }}
                                </div>
                                <span v-else>-</span>
                            </td>
                            <td class="uk-text-right">
                                <div
                                        v-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]  && !getValueForMonth(month, 'has_payment')  && canEditLedgerAmounts">
                                    <currency-input :currency="{ prefix: '$ '}" v-if="canChangeAmount"
                                           @change="updateValueForMonth(month, 'paid', $event)"
                                           :value="getValueForMonth(month, 'paid')"/>
                                    <span v-else class="uk-text-right">{{ getValueForMonth(month, 'paid') | formatMoney}}</span>
                                </div>
                                <div
                                        v-else-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]">
                                    {{ getValueForMonth(month, 'paid') | formatMoney }}
                                </div>
                                <span v-else>-</span>
                            </td>
                            <td class="uk-text-right" v-if="program.settings['uses-ledger-entry-fees']">
                                <div
                                        v-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]  && !getValueForMonth(month, 'has_payment')  && canEditLedgerAmounts">
                                    <currency-input :currency="{ prefix: '$ '}" v-if="canChangeAmount"
                                           @change="updateValueForMonth(month, 'fees', $event)"
                                           :value="getValueForMonth(month, 'fees')"/>
                                    <span v-else class="uk-text-right">{{ getValueForMonth(month, 'fees') | formatMoney}}</span>

                                </div>
                                <div
                                        v-else-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]">
                                    {{ getValueForMonth(month, 'fees') | formatMoney }}
                                </div>
                                <span v-else>-</span>
                            </td>
                            <td class="uk-text-right" v-if="showAlternativeFunding">
                                <div v-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]">
                                    <div class="uk-margin-bottom"
                                         v-for="(program, i) in getArrayValueForMonth(month, 'alternative_funding_programs')">
                                        <currency-input :currency="{ prefix: '$ '}"  v-model="program.pivot.amount"/>
                                        <select placeholder="Select Source"
                                                v-model="program.pivot.alternative_funding_program_id"
                                                class="uk-input uk-select" style="width: 175px;">
                                            <option :value="null">Choose a Source...</option>
                                            <option v-for="_program in alternativeFundingPrograms" :value="_program.id">
                                                {{ _program.name }}
                                            </option>
                                        </select>
                                        <a href="javascript:;" class="uk-text-danger"
                                           v-if=" canEditLedgerAmounts"
                                           @click="monthInputs[month.format(monthFormat)].alternative_funding_programs.splice(i, 1)"><i
                                                class="a-trash-2"></i></a>
                                    </div>
                                    <a href="javascript:;"
                                       v-if="canEditLedgerAmounts"
                                       @click="addAssistance(monthInputs[month.format(monthFormat)].alternative_funding_programs)"><i
                                            class="a-circle-plus"></i> Report Other Funding Received</a>

                                </div>
                                <span v-else>-</span>
                            </td>
                            <td class="uk-text-right">
                                <div v-if="checkedMonths.includes(month.format(monthFormat))" class="uk-text-right">
                                    {{ getRequestedAssistanceForMonth(month) | formatMoney }}
                                </div>
                                <span v-else>-</span>
                            </td>
                            <template v-if="permissions.processor">
                                <td class="uk-text-right">
                                    <span
                                            v-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]">
                                        <span v-if="getValueForMonth(month, 'has_payment')">
                                            {{ getValueForMonth(month, 'approved') | formatMoney }}
                                        </span>
                                        <span v-else>
                                            <currency-input :currency="{ prefix: '$ '}"
                                                   @change="updateValueForMonth(month, 'approved', $event)"
                                                   :value="getValueForMonth(month, 'approved')"/>
                                        </span>
                                    </span>
                                    <span v-else>-</span>
                                </td>

                                <td class="uk-text-right">
                                    <span
                                            v-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]">
                                        {{ getValueForMonth(month, 'paid_by_sponsor') | formatMoney }}
                                    </span>
                                    <span v-else>-</span>
                                </td>
                            </template>
                        </tr>
                        <tr v-if="permissions.processor && showLLEdit(month)" class="color-green" style="font-weight:bold">
                            <td>LL Edit</td>
                            <td class="uk-text-right">
                                <div v-if="getValueForMonth(month, 'landlord_edited_amount') != getValueForMonth(month, 'amount')">
                                    {{ getValueForMonth(month, 'landlord_edited_amount') | formatMoney }}
                                </div>
                            </td>
                            <td class="uk-text-right">
                                <div  v-if="getValueForMonth(month, 'landlord_edited_paid') != getValueForMonth(month, 'paid')">
                                    {{ getValueForMonth(month, 'landlord_edited_paid') | formatMoney }}
                                </div>
                            </td>
                            <td class="uk-text-right">
                                <div v-if="getValueForMonth(month, 'landlord_edited_fees') != getValueForMonth(month, 'fees')">
                                    {{ getValueForMonth(month, 'landlord_edited_fees') | formatMoney }}
                                </div>
                            </td>
                            <td class="uk-text-right" v-if="showAlternativeFunding">
                                <div v-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]">
                                    <div class="uk-margin-bottom">
                                        <div v-for="(program, i) in getLLAlternative(month)" :key="i">
                                            {{ program.pivot.amount | formatMoney }} - {{ getAlernativeFundProgramName(program.pivot.alternative_funding_program_id)}}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td class="uk-text-right"></td>
                            <td class="uk-text-right"></td>
                        </tr>
                        </template>
                        </tbody>
                    </table>

                    <p v-if="futureMonths() > 1">*You may apply for {{ futureMonths() }} future months of assistance
                        under this
                        program.</p>
                </div>
                <div
                        class="uk-grid uk-width-small-1-1 uk-hidden-medium uk-hidden-large uk-form uk-form-horizontal uk-form-stacked">
                    <div class="uk-width-1-1 uk-grid" v-for="month in months">
                        <div class="uk-width-1-1">
                            <h4>
                                <input type="checkbox" :value="month.format(monthFormat)" v-model="checkedMonths"/>
                                {{ month.format('MMM YYYY') }}
                            </h4>

                        </div>
                        <div class="uk-width-1-1 uk-margin-top uk-margin-large-bottom"
                             v-if="checkedMonths.includes(month.format(monthFormat))">
                            <div class="uk-form-row">
                                <label class="uk-form-label" for="">{{ month.format('MMMM') }} {{ type.name }}
                                    {{ programSettings['ledger-title-amount-due'] }}</label>
                                <div class="uk-form-controls">
                                    <currency-input :currency="{ prefix: '$ '}"
                                           @change="updateValueForMonth(month, 'amount', $event)"
                                           :value="getValueForMonth(month, 'amount')"/>
                                </div>
                            </div>
                            <div class="uk-form-row">
                                <label class="uk-form-label" for="">{{ month.format('MMMM') }} {{ type.name }}
                                    {{ programSettings['ledger-title-amount-paid'] }}</label>
                                <div class="uk-form-controls">
                                    <currency-input :currency="{ prefix: '$ '}"
                                           @change="updateValueForMonth(month, 'paid', $event)"
                                           :value="getValueForMonth(month, 'paid')"/>
                                </div>
                            </div>
                            <div class="uk-form-row" v-if="program.settings['uses-ledger-entry-fees']">
                                <label class="uk-form-label" for="">
                                    {{ month.format('MMMM') }} {{ type.name }}
                                    {{ programSettings['ledger-title-amount-fees-due'] }}
                                    <span v-if="type.pivot.max_fee_amount" class="uk-text-muted">
                                    (Max {{ type.pivot.max_fee_amount | formatMoney }})
                                </span>
                                </label>
                                <div class="uk-form-controls">
                                    <currency-input :currency="{ prefix: '$ '}"
                                           @change="updateValueForMonth(month, 'fees', $event)"
                                           :value="getValueForMonth(month, 'fees')"/>
                                </div>
                            </div>
                            <div class="uk-form-row"
                                 v-if="application.receiving_other_assistance && programSettings['uses-duplicative-assistance']">
                                <div v-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]">
                                    <label class="uk-form-label" for="">{{ month.format('MMMM') }} {{ type.name }} Other
                                        Assistance
                                        Received</label>
                                    <div class="uk-margin-bottom"
                                         v-for="(program, i) in monthInputs[month.format(monthFormat)].alternative_funding_programs">
                                        <currency-input :currency="{ prefix: '$ '}" v-model="program.pivot.amount"/>
                                        <select placeholder="Select Source"
                                                v-model="program.pivot.alternative_funding_program_id"
                                                class="uk-input uk-select" style="width: 175px;">
                                            <option :value="null">Choose a Source...</option>
                                            <option v-for="_program in alternativeFundingPrograms" :value="_program.id">
                                                {{
                                                    _program.name
                                                }}
                                            </option>
                                        </select>
                                        <a href="javascript:;" class="uk-text-danger"
                                           @click="monthInputs[month.format(monthFormat)].alternative_funding_programs.splice(i, 1)"><i
                                                class="a-trash-2"></i></a>
                                    </div>
                                    <a href="javascript:;"
                                       @click="addAssistance(monthInputs[month.format(monthFormat)].alternative_funding_programs)"><i
                                            class="a-circle-plus"></i> Report Other Funding Received</a>

                                </div>
                            </div>
                            <div class="uk-form-row">
                                <label class="uk-form-label" for="">{{ month.format('MMMM') }}
                                    {{ programSettings['ledger-title-amount-total-requested'] }}</label>
                                <div class="uk-form-controls">
                                    <strong>{{ getRequestedAssistanceForMonth(month) | formatMoney }}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="uk-width-1-1 uk-margin-top" v-if="type.pivot.type === 2 && needsAssistance">
                <div class="uk-alert" style="padding: 16px;">
                    {{ type.pivot.flat_rate_disclaimer }}
                </div>

                <div>
                    <h3 class="uk-text-bold">Total {{ type.name }} Assistance Available:
                        {{ type.pivot.flat_rate_amount | formatMoney }}</h3>
                </div>
            </div>
            <div class="uk-width-1-1 uk-margin-top" v-else-if="type.pivot.type === 3 && needsAssistance">
                <div class="uk-overflow-container uk-hidden-small uk-grid-width-medium-1-1">
                    <table class="uk-table uk-table-divider uk-table-striped">
                        <thead>
                        <tr>
                            <th>Month<span v-if="futureMonths() > 1">*</span></th>
                            <th>Monthly Flat Rate</th>
                            <th class="uk-text-right">Total Requested</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="month in months">
                            <td>
                                <input type="checkbox" :value="month.format(monthFormat)"
                                       @change="onMonthCheckedChange(month, $event)"
                                       v-model="checkedMonths"/>
                                {{ month.format('MMM YYYY') }}
                            </td>
                            <td>
                                <div v-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]">
                                    <currency-input :currency="{ prefix: '$ '}"
                                           @change="updateValueForMonth(month, 'amount', $event)"
                                           :value="getValueForMonth(month, 'amount')"/>
                                </div>
                                <span v-else>-</span>
                            </td>
                            <td class="uk-text-right">
                                <div v-if="checkedMonths.includes(month.format(monthFormat))" class="uk-text-right">
                                    {{ getRequestedAssistanceForMonth(month) | formatMoney }}
                                </div>
                                <span v-else>-</span>
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <p v-if="futureMonths() > 1">*You may apply for {{ futureMonths() }} future months of assistance
                        under this
                        program.</p>
                </div>
                <div
                        class="uk-grid uk-width-small-1-1 uk-hidden-medium uk-hidden-large uk-form uk-form-horizontal uk-form-stacked">
                    <div class="uk-width-1-1 uk-grid" v-for="month in months">
                        <div class="uk-width-1-1">
                            <h4>
                                <input type="checkbox" :value="month.format(monthFormat)" v-model="checkedMonths"/>
                                {{ month.format('MMM YYYY') }}
                            </h4>

                        </div>
                        <div class="uk-width-1-1 uk-margin-top uk-margin-large-bottom"
                             v-if="checkedMonths.includes(month.format(monthFormat))">
                            <div class="uk-form-row">
                                <label class="uk-form-label" for="">{{ month.format('MMMM') }} {{ type.name }}
                                    {{ programSettings['ledger-title-amount-due'] }}</label>
                                <div class="uk-form-controls">
                                    <currency-input :currency="{ prefix: '$ '}"
                                           @change="updateValueForMonth(month, 'amount', $event)"
                                           :value="getValueForMonth(month, 'amount')"/>
                                </div>
                            </div>
                            <div class="uk-form-row">
                                <label class="uk-form-label" for="">{{ month.format('MMMM') }} {{ type.name }}
                                    {{ programSettings['ledger-title-amount-paid'] }}</label>
                                <div class="uk-form-controls">
                                    <currency-input :currency="{ prefix: '$ '}"
                                           @change="updateValueForMonth(month, 'paid', $event)"
                                           :value="getValueForMonth(month, 'paid')"/>
                                </div>
                            </div>
                            <div class="uk-form-row" v-if="program.settings['uses-ledger-entry-fees']">
                                <label class="uk-form-label" for="">
                                    {{ month.format('MMMM') }} {{ type.name }}
                                    {{ programSettings['ledger-title-fees-due'] }}
                                    <span v-if="type.pivot.max_fee_amount" class="uk-text-muted">
                                    (Max {{ type.pivot.max_fee_amount | formatMoney }})
                                </span>
                                </label>
                                <div class="uk-form-controls">
                                    <currency-input :currency="{ prefix: '$ '}"
                                           @change="updateValueForMonth(month, 'fees', $event)"
                                           :value="getValueForMonth(month, 'fees')"/>
                                </div>
                            </div>
                            <div class="uk-form-row"
                                 v-if="application.receiving_other_assistance && programSettings['uses-duplicative-assistance']">
                                <div v-if="checkedMonths.includes(month.format(monthFormat)) && monthInputs[month.format(monthFormat)]">
                                    <label class="uk-form-label" for="">{{ month.format('MMMM') }} {{ type.name }} Other
                                        Assistance
                                        Received</label>
                                    <div class="uk-margin-bottom"
                                         v-for="(program, i) in monthInputs[month.format(monthFormat)].alternative_funding_programs">
                                        <currency-input :currency="{ prefix: '$ '}" v-model="program.pivot.amount"/>
                                        <select placeholder="Select Source"
                                                v-model="program.pivot.alternative_funding_program_id"
                                                class="uk-input uk-select" style="width: 175px;">
                                            <option :value="null">Choose a Source...</option>
                                            <option v-for="_program in alternativeFundingPrograms" :value="_program.id">
                                                {{
                                                    _program.name
                                                }}
                                            </option>
                                        </select>
                                        <a href="javascript:;" class="uk-text-danger"
                                           @click="monthInputs[month.format(monthFormat)].alternative_funding_programs.splice(i, 1)"><i
                                                class="a-trash-2"></i></a>
                                    </div>
                                    <a href="javascript:;"
                                       @click="addAssistance(monthInputs[month.format(monthFormat)].alternative_funding_programs)"><i
                                            class="a-circle-plus"></i> Report Other Funding Received</a>

                                </div>
                            </div>
                            <div class="uk-form-row">
                                <label class="uk-form-label" for="">{{ month.format('MMMM') }}
                                    {{ programSettings['ledger-title-amount-due'] }}</label>
                                <div class="uk-form-controls">
                                    <strong>{{ getRequestedAssistanceForMonth(month) | formatMoney }}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="uk-width-1-1 uk-margin-top" v-else>
                <validated-section :errors="errors" field="total_request">
                    <h3 class="uk-text-bold">Total {{ type.name }} Assistance Requested: {{
                            totalRequested | formatMoney
                        }}</h3>
                    <template v-if="permissions.processor">
                        <h3 class="uk-text-bold">Total {{ type.name }} Assistance Approved:
                            {{ totalApproved | formatMoney }}</h3>
                        <h3 class="uk-text-bold">Total {{ type.name }} Assistance Paid: {{
                                totalPaid | formatMoney
                            }}</h3>
                    </template>
                </validated-section>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapState, mapGetters } from "vuex";
    import moment from 'moment';
    import ValidatedSection from "../../Common/ValidatedSection";
    import Autocomplete from '@trevoreyre/autocomplete-vue';
    export default {
        name: "LedgerEntriesForType",
        components: {ValidatedSection, Autocomplete},
        props: {
            type: {
                type: Object,
                required: true,
            },
            entries: {
                type: Array,
                default() {
                    return [];
                }
            },
            showDisconnect: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                errors: {},
                needsAssistance: false,
                checkedMonths: [],
                monthInputs: {},
                now: moment(),
                monthFormat: 'YYYY-MM',
                hhMemberId: null,
                disconnectStatusId: null,
                vendorId: null,
                vendor: null,
                accountNumber: null,
                accountNumberConfirmation: null,
                vendors: [],
            }
        },
        computed: {
            showAlternativeFunding() {
                return this.application.receiving_other_assistance && this.programSettings['uses-duplicative-assistance']
            },
            canChangeAmount() {
                let canChange = false;
                let allowed = this.program.settings['user-roles-to-edit-assistance-values'];
                if(this.user !== null && this.user !== undefined){
                    for(let j in this.user.roles) {
                        let role = this.user.roles[j];
                        for(var i in allowed) {
                            let allowedRoleId = parseInt(allowed[i]);
                            if(role.role_id === allowedRoleId) {
                                canChange = true;
                            }
                        }

                        if(role.role_id === 6) {
                            canChange = true;
                        }
                    }
                }


                return canChange;
            },
          canEditLedgerAmounts() {
              return this.canChangeAmount;
          },
          canShowLedger() {
              if(this.type.id === 1) {
                  return this.application.ami_subsidy_adjusted !== 0 || this.programSettings['ami-blocks-rent-entries'] === false;
              }
              return this.type.pivot.type === 1 || this.pivot.type === 3;
          },
          months() {
            let start = moment(this.program.assistance_start);
            let end = moment(this.program.assistance_end);
            let added = parseInt(this.programSettings['ledger-entry-future-months']);
            if (this.type.pivot.additional_months) {
                added = parseInt(this.type.pivot.additional_months);
            }
            let endToShow = this.now.clone().add(added, 'months');
            let months = [];
            while (start.isBefore(end)) {

                if (start.isAfter(endToShow)) break;
                if (start.isAfter(end)) break;

                months.push(start.clone());

                start.add(1, 'month');
            }

            return months;
        },
        totalRequested() {
            let total = 0;
            _.each(this.months, month => {
                total += this.getRequestedAssistanceForMonth(month);
            })
            return total;
        },
        totalApproved() {
            let total = 0;
            _.each(this.months, month => {
                total += this.getValueForMonth(month, 'approved');
            })
            return total;
        },
        totalPaid() {
            return this.getTotalPaid()
        },
        entries() {
            return _.filter(this.unitLedgerEntries, entry => entry.unit_ledger_entry_type_id === this.type.id)
        },
        ...mapState('App', {
            permissions: state => state.permissions,
            user: state => state.user,
        }),
        ...mapState('Application', {
            assets: state => state.assets,
            unitLedgerEntries: state => state.application ? state.application.unit_ledger_entries : null,
            landlordConfirmations: state => state.landlordConfirmations,
            application: state => state.application,
            program: state => state.application.program,
            householdMembers: state => state.hhMembers,
            alternativeFundingPrograms: state => state.assets.alternativeFundingPrograms,
        }),
        ...mapGetters('Application', ['programSettings']),
    },
    watch: {
        needsAssistance(newValue) {
            if (newValue === false) {
                this.monthInputs = {};
                this.checkedMonths = [];
            } else {
                if (this.type.pivot.type === 2) {
                    this.monthInputs[this.months[0].format(this.monthFormat)] = {
                        amount: this.type.pivot.flat_rate_amount,
                        fees: 0,
                        paid: 0,
                        alternative_funding_programs: [],
                    }
                }
            }
            this.$emit('updated', this.monthInputs)
        },
        entries(newValue) {
            this.setDataFromEntries();
        },
    },
    async mounted() {
        // await this.getVendors();
        if (this.entries) {
            this.setDataFromEntries();
        }
    },
    methods: {
        getAlernativeFundProgramName(id) {
            const p = this.alternativeFundingPrograms.find(item => item.id == id);
            return p ? p.name : 'N/A';
        },
        getLLAlternative(month)
        {
            const LLAlternativeFundingProgram = this.getArrayValueForMonth(month, 'landlord_edited_alternative_funding_programs');
            return LLAlternativeFundingProgram;
        },
        isAlternativeEdited(month, i)
        {
            let show = false;
            const LLAlternativeFundingProgram = this.getArrayValueForMonth(month, 'landlord_edited_alternative_funding_programs')
            if (LLAlternativeFundingProgram.hasOwnProperty(i) && LLAlternativeFundingProgram[i].pivot.amount != this.getArrayValueForMonth(month, 'alternative_funding_programs')[i].pivot.amount)
            {
                show = true;
            }
            return show;
        },

        showLLEdit(month)
        {
            let show = false;
            let confirmationValues = this.getConfirmationValuesForMonth(month.format('YYYY-MM'));
            if (!this.type.is_utility && confirmationValues)
            {
                show = this.getValueForMonth(month, 'landlord_edited_amount') != this.getValueForMonth(month, 'amount') ||
                    this.getValueForMonth(month, 'landlord_edited_paid') != this.getValueForMonth(month, 'paid') ||
                    this.getValueForMonth(month, 'landlord_edited_fees') != this.getValueForMonth(month, 'fees');

                const alternativeFundingProgram = this.getArrayValueForMonth(month, 'alternative_funding_programs'),
                    LLAlternativeFundingProgram = this.getArrayValueForMonth(month, 'landlord_edited_alternative_funding_programs');
                const count = Math.max(alternativeFundingProgram.length, LLAlternativeFundingProgram.length)
                for (var i=0; i< count; i++)
                {
                    if (LLAlternativeFundingProgram.hasOwnProperty(i))
                    {
                        if (!alternativeFundingProgram.hasOwnProperty(i) || LLAlternativeFundingProgram[i].pivot.amount != alternativeFundingProgram[i].pivot.amount)
                        {
                            show = show || true;
                        }
                    }
                }
            }

            return show;
        },

        getTotalPaid() {
            let total = 0;
            _.each(this.months, month => {
                total += this.getValueForMonth(month, 'paid_by_sponsor');
            })
            return total;
        },
        getCheckboxText(type) {
            if (type.pivot && type.pivot.custom_checkbox_text)
                return type.pivot.custom_checkbox_text
            return `Do You Need Assistance for Your ${type.name}?`
        },

        setDefaultVendor(id) {
            axios.get(`/api/hope/vendor/${id}`)
                .then((r) => {
                    if (r.data) {
                        this.vendor = r.data
                        this.$nextTick(() => {
                            if (this.$refs.autocomplete) {
                                this.$refs.autocomplete.value = this.vendor.organization_name;
                            }
                        })
                    }
                })
        },

        async getVendors() {
            // let r = await axios.get(`/api/hope/vendors?entry_type_id=${this.type.id}`);
            // this.vendors = r.data;
        },
        async searchVendors(query) {
            if (query.length < 3) return [];
            let r = await axios.get(`/api/hope/vendors?entry_type_id=${this.type.id}&q=${query}&usecase=ledger_entry_vendor_search`);
            return r.data;
        },
        getResultValue(result) {
            return result.organization_name;
        },
        setVendor(result) {
            this.vendors.push(result);
            this.vendor = result;
            this.vendorId = result.id;
        },
        async setVendorFromTextValue($event) {
            if ($event.target.value && !this.vendor) {
                UIkit.modal.confirm(`If you don't see your vendor/provider, please select "Other, Please See Bill".`, async () => {
                    this.vendorId = null;
                    this.$refs.autocomplete.value = '';
                }, () => {
                    this.vendorId = null;
                    this.$refs.autocomplete.value = '';
                }, {
                    labels: {
                        'Ok': 'Ok',
                        'Cancel': 'Dismiss',
                    }
                });
            }
        },
        onMonthCheckedChange(month, $event) {
            if ($event.target.checked === false) {
                this.$delete(this.monthInputs, month.format(this.monthFormat));
            } else {
                let amount = 0;
                if (this.type.pivot.type === 3) {
                    amount = parseFloat(this.type.pivot.flat_rate_amount);
                }

                if (this.monthInputs[month.format(this.monthFormat)] === undefined) {
                    this.$set(this.monthInputs, month.format(this.monthFormat), {
                        amount: amount,
                        fees: 0,
                        paid: 0,
                        approved: 0,
                        paid_by_sponsor: 0,
                        alternative_funding_programs: [],
                    });
                }
            }
            this.$emit('updated', this.monthInputs)
        },
        getConfirmationValuesForMonth(monthString) {
            for (var i in this.landlordConfirmations) {
                let confirmation = this.landlordConfirmations[i];
                if (confirmation.edited_amount_meta && confirmation.edited_amount_meta[monthString]) {
                    return confirmation.edited_amount_meta[monthString];
                }
            }
            return null;
        },
        setDataFromEntries() {
            _.each(this.entries, entry => {
                if (!this.needsAssistance) {
                    this.needsAssistance = true;
                }
                let formattedDate = moment(entry.due_date).format(this.monthFormat);

                let confirmationValues;
                if (this.type.name === 'Rent') {
                    confirmationValues = this.getConfirmationValuesForMonth(moment(entry.due_date).format('YYYY-MM'));
                }

                let assistanceApprovedAmount;
                if (entry.pivot.assistance_amount_approved !== undefined) {
                    assistanceApprovedAmount = parseFloat(entry.pivot.assistance_amount_approved);
                } else if (confirmationValues) {
                    assistanceApprovedAmount = parseFloat(confirmationValues.due) + parseFloat(confirmationValues.fees) - parseFloat(confirmationValues.paid);
                    if (confirmationValues.alternative_funding_programs && confirmationValues.alternative_funding_programs.length) {
                        _.each(confirmationValues.alternative_funding_programs, afp => {
                            assistanceApprovedAmount -= parseFloat(afp.pivot.amount);
                        })
                    }
                } else {
                    assistanceApprovedAmount = parseFloat(entry.amount_due) + parseFloat(entry.fees_due) - parseFloat(entry.amount_paid);
                    if (entry.alternative_funding_programs && entry.alternative_funding_programs.length) {
                        _.each(entry.alternative_funding_programs, afp => {
                            assistanceApprovedAmount -= parseFloat(afp.pivot.amount);
                        })
                    }
                }

                this.$set(this.monthInputs, formattedDate, {
                    amount: parseFloat(entry.amount_due),
                    fees: parseFloat(entry.fees_due),
                    paid: parseFloat(entry.amount_paid),
                    other_funding_amount: parseFloat(entry.other_funding_amount),
                    other_funding_source_id: parseInt(entry.other_funding_source_id),
                    alternative_funding_programs: entry.alternative_funding_programs,
                    approved: assistanceApprovedAmount,
                    paid_by_sponsor: entry.batch_vendor_payment ? parseFloat(entry.batch_vendor_payment.amount_paid_by_sponsor) : 0,
                    has_payment: entry.batch_vendor_payment ? true : false,
                    landlord_edited_amount: confirmationValues ? confirmationValues.due : null,
                    landlord_edited_fees: confirmationValues ? confirmationValues.fees : null,
                    landlord_edited_paid: confirmationValues ? confirmationValues.paid : null,
                    landlord_edited_alternative_funding_programs: confirmationValues ? confirmationValues.alternative_funding_programs: [],

                });

                this.checkedMonths.push(formattedDate);

                if (entry.household_member_id) {
                    this.hhMemberId = entry.household_member_id;
                }
                if (entry.disconnect_status_id !== null) {
                    this.disconnectStatusId = entry.disconnect_status_id;
                }
                if (entry.organization && !this.vendorId) {
                    this.vendorId = entry.organization.id;
                    let vendor = entry.organization;
                    if (vendor) {
                        this.$nextTick(() => {
                          if(this.$refs.autocomplete) {
                            this.$refs.autocomplete.value = vendor.organization_name;
                          }
                        })
                    }
                }

                if (entry.account_number && !this.accountNumber) {
                    this.accountNumber = entry.account_number;
                    this.accountNumberConfirmation = entry.account_number;
                }
            })

            // const memberIndex = this.application.household.members.findIndex(item => item.id == this.hhMemberId)
            // if (memberIndex != -1 && this.landlordConfirmations[memberIndex])
            // {
            //     _.each(this.landlordConfirmations[memberIndex].edited_amount_meta, (item, date) => {
            //         if (!this.checkedMonths.hasOwnProperty(date) && item && (item.due || item.fees || item.paid || item.alternative_funding_programs))
            //         {
            //             this.$set(this.monthInputs, date, {
            //                 amount: '',
            //                 fees: '',
            //                 paid: '',
            //                 other_funding_amount: '',
            //                 other_funding_source_id: '',
            //                 alternative_funding_programs: [],
            //                 approved: '',
            //                 paid_by_sponsor: 0,
            //                 has_payment: false,
            //                 landlord_edited_amount: item.due,
            //                 landlord_edited_fees: item.fees,
            //                 landlord_edited_paid: item.paid,
            //                 landlord_edited_alternative_funding_programs: item.alternative_funding_programs ? item.alternative_funding_programs: [],

            //             });

            //             this.checkedMonths.push(date);
            //         }
            //     })
            // }
        },

        getValueForMonth(month, field) {
            if (this.monthInputs[month.format(this.monthFormat)] !== undefined) {
                return this.monthInputs[month.format(this.monthFormat)][field] ? this.monthInputs[month.format(this.monthFormat)][field] : 0;
            }
            return 0;
        },

        getArrayValueForMonth(month, field) {
            if (this.monthInputs[month.format(this.monthFormat)] !== undefined) {
                return this.monthInputs[month.format(this.monthFormat)][field] ? this.monthInputs[month.format(this.monthFormat)][field] : [];
            }
            return [];
        },

        updateValueForMonth(month, field, value) {
            if (value === '') value = 0;
            value = parseFloat(value);
            if (field === 'fees') {
                let maxAmount = this.type.pivot.max_fee_amount;
                if (maxAmount !== null && value > parseFloat(maxAmount)) {
                    UIkit.modal.alert('The maximum fee amount for this assistance type is $' + maxAmount + '. We have adjusted your fee amount accordingly. ');
                    value = maxAmount;
                }
            }
            if (this.monthInputs[month.format(this.monthFormat)] === undefined) {
                this.$set(this.monthInputs, month.format(this.monthFormat), {
                    amount: 0,
                    fees: 0,
                    paid: 0,
                    alternative_funding_programs: [],
                });
            }
            this.monthInputs[month.format(this.monthFormat)][field] = parseFloat(value);

            this.$emit('updated', this.monthInputs)
        },

        getRequestedAssistanceForMonth(month) {
            let monthInput = this.monthInputs[month.format(this.monthFormat)];
            if (monthInput !== undefined) {
                let amount = parseFloat(monthInput.amount) + parseFloat(monthInput.fees) - parseFloat(monthInput.paid);

                if (monthInput.alternative_funding_programs && monthInput.alternative_funding_programs.length) {
                    _.each(monthInput.alternative_funding_programs, afp => {
                        amount -= afp.pivot.amount;
                    })
                }

                return amount;
            }
            return 0;
        },

        addAssistance(programs) {
            programs.push({
                pivot: {
                    alternative_funding_program_id: null,
                    amount: 0,
                }
            })
        },

        getMonthInputs() {
            return this.monthInputs;
        },

        getHouseholdMemberId() {
            return this.hhMemberId;
        },

        getDisconnectStatusId() {
            return this.disconnectStatusId;
        },

        getVendorId() {
            return this.vendorId;
        },

        getVendorName() {
            if (this.$refs.autocomplete) {
                return this.$refs.autocomplete.value;
            }
        },

        getAccountNumber() {
            return this.accountNumber;
        },

        clearAssistance() {
            // this.monthInputs = [];
            // this.checkedMonths = [];
        },

        futureMonths() {
            // let now = moment();
            // let end = moment(this.program.assistance_end);
            // return Math.round((end - now) / 2692000000); //convert to months
            if (this.type.pivot.additional_months) {
                return parseInt(this.type.pivot.additional_months);
            }
            return parseInt(this.programSettings['ledger-entry-future-months']);
        },

        async validate() {
            this.errors = {};

            if (this.needsAssistance) {
                if (!this.hhMemberId) {
                    this.errors['hhMemberId'] = ['You must select who pays this bill.'];
                }

                if (this.type.is_utility) {
                    if (this.showDisconnect && !this.disconnectStatusId) {
                        this.errors['disconnectStatusId'] = ['You must make a selection.'];
                    }

                    if (!this.vendorId && !this.$refs.autocomplete.value) {
                        this.errors['vendorId'] = ['You must select a vendor.'];
                    }

                    if (!this.accountNumber) {
                        this.errors['accountNumber'] = ['You must enter an account number.']
                    }

                    if (this.accountNumber !== this.accountNumberConfirmation) {
                        this.errors['accountNumberConfirmation'] = ['Your account numbers must match.']
                    }
                }

                if (this.totalRequested == 0 && this.type.pivot.type === 1) {
                    if (this.type.name == 'Rent' && this.programSettings['ami-blocks-rent-entries'] === true) {
                        // do not block
                    } else {
                        this.errors['total_request'] = ['You must check at least one option above.'];
                    }
                }
            }

            return this.errors;
        }

    }
}
</script>
